import React, { useState, useRef, useCallback } from 'react';

interface ImageFile {
  file: File;
  name: string;
  size: number;
  type: string;
  base64: string;
}

const ImageToBase64: React.FC = () => {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [converting, setConverting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert to base64'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFiles = useCallback(async (files: FileList) => {
    setConverting(true);
    const newImages: ImageFile[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        try {
          const base64 = await convertToBase64(file);
          newImages.push({
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            base64
          });
        } catch (error) {
          console.error('Error converting file:', file.name, error);
        }
      }
    }
    
    setImages(prev => [...prev, ...newImages]);
    setConverting(false);
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCopyBase64 = (base64: string) => {
    navigator.clipboard.writeText(base64);
  };

  const handleDownloadBase64 = (base64: string, filename: string) => {
    const link = document.createElement('a');
    link.href = base64;
    link.download = `${filename.replace(/\.[^/.]+$/, '')}_base64.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getBase64Size = (base64: string): string => {
    const sizeInBytes = Math.ceil((base64.length * 3) / 4);
    return formatFileSize(sizeInBytes);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">图片转Base64工具</h1>
          <p className="text-lg text-gray-600">将图片文件转换为Base64编码，支持拖拽上传和批量处理</p>
        </div>

        {/* Upload Area */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="text-6xl mb-4">🖼️</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {dragActive ? '释放文件以上传' : '拖拽图片文件到此处'}
            </h3>
            <p className="text-gray-600 mb-6">
              支持 JPG、PNG、GIF、WebP 等常见图片格式
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              选择文件
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInput}
              className="hidden"
            />
          </div>
        </div>

        {/* Converting Status */}
        {converting && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-blue-800">正在转换图片...</span>
            </div>
          </div>
        )}

        {/* Results */}
        {images.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">转换结果</h2>
              <button
                onClick={() => setImages([])}
                className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
              >
                清空所有
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {images.map((image, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* Image Preview */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-800 truncate">{image.name}</h3>
                      <button
                        onClick={() => handleRemoveImage(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                      <span>类型: {image.type}</span>
                      <span>大小: {formatFileSize(image.size)}</span>
                    </div>
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={image.base64}
                        alt={image.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  {/* Base64 Info */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Base64 编码</span>
                      <span className="text-xs text-gray-500">
                        大小: {getBase64Size(image.base64)}
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      <button
                        onClick={() => handleCopyBase64(image.base64)}
                        className="w-full px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm font-medium"
                      >
                        复制 Base64
                      </button>
                      
                      <button
                        onClick={() => handleDownloadBase64(image.base64, image.name)}
                        className="w-full px-3 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors text-sm font-medium"
                      >
                        下载 Base64 文件
                      </button>
                    </div>

                    {/* Base64 Preview */}
                    <div className="mt-3">
                      <textarea
                        value={image.base64}
                        readOnly
                        className="w-full h-24 p-2 text-xs bg-gray-50 border border-gray-200 rounded resize-none font-mono"
                        placeholder="Base64 编码将显示在这里..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">🚀</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">快速转换</h3>
            <p className="text-gray-600">支持拖拽上传，一键转换为Base64编码</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">📋</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">便捷操作</h3>
            <p className="text-gray-600">支持复制、下载Base64编码，满足不同使用需求</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">🖼️</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">多格式支持</h3>
            <p className="text-gray-600">支持JPG、PNG、GIF、WebP等常见图片格式</p>
          </div>
        </div>

        {/* Usage Tips */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">使用说明</h3>
          <ul className="text-blue-700 space-y-2 text-sm">
            <li>• 支持拖拽上传图片文件，也可以点击选择文件按钮</li>
            <li>• 转换后的Base64编码可以直接复制使用</li>
            <li>• 可以下载Base64编码到本地文件</li>
            <li>• 支持批量处理多个图片文件</li>
            <li>• Base64编码会显示文件大小信息，方便了解数据量</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ImageToBase64;
